// Small building blocks for the admin session panel (BookingAdmin): consent
// icons, medical notes, the per-participant Emergency button and the session
// actions menu.
import React, { useState, useEffect, useRef } from 'react';
import {
  CameraIcon,
  ShareIcon,
  PhoneIcon,
  EllipsisHorizontalIcon,
  HeartIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/20/solid';
import './sessionPanel.css';

const waLink = (phone) => `https://wa.me/${phone.replace(/\D/g, '').replace(/^0/, '44')}`;
const telLink = (phone) => `tel:${phone.replace(/[^\d+]/g, '')}`;

// Close a popover on outside click/tap or Escape.
function useDismiss(open, setOpen) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, setOpen]);
  return ref;
}

const CONSENTS = [
  { type: 'photo_coaching', label: 'Coaching photo consent', Icon: CameraIcon },
  { type: 'photo_social_media', label: 'Social media photo consent', Icon: ShareIcon },
];

export function ConsentIcons({ consents }) {
  return (
    <span className="sp-consents">
      {CONSENTS.map(({ type, label, Icon }) => {
        const granted = Boolean(consents?.find(c => c.type === type)?.granted);
        const text = `${label}: ${granted ? 'given' : 'not given'}`;
        return (
          <span
            key={type}
            className={`sp-consent ${granted ? 'sp-consent--yes' : 'sp-consent--no'}`}
            title={text}
            aria-label={text}
            role="img"
          >
            <Icon aria-hidden="true" />
          </span>
        );
      })}
    </span>
  );
}

// 'none' is what the "No known medical conditions" checkbox saves; families
// also type things like "N/A" or "Nil" into the free-text box.
const NO_NOTES = /^(none|n\/?a|nil|no|nothing|-+)\.?$/i;
const hasMedicalNotes = (notes) => Boolean(notes && notes.trim() && !NO_NOTES.test(notes.trim()));

// A small, muted button that reveals medical notes on tap, so they aren't on
// show to anyone glancing at the coach's screen.
export function MedicalNotes({ notes }) {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, setOpen);
  if (!hasMedicalNotes(notes)) return null;
  return (
    <span className="sp-popover-anchor" ref={ref}>
      <button
        type="button"
        className="sp-chip sp-chip--medical"
        aria-expanded={open}
        aria-label="Medical notes"
        onClick={() => setOpen(o => !o)}
      >
        <HeartIcon aria-hidden="true" />
        Medical
      </button>
      {open && (
        <span className="sp-popover sp-medical" role="dialog" aria-label="Medical notes">
          {notes}
        </span>
      )}
    </span>
  );
}

// contacts: [{ role, name, relationship?, phone }]. Shows a warning style when
// none of them has a phone number.
export function EmergencyButton({ contacts }) {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, setOpen);
  const reachable = contacts.some(c => c.phone);

  return (
    <span className="sp-popover-anchor" ref={ref}>
      <button
        type="button"
        className={`sp-chip${reachable ? '' : ' sp-chip--missing'}`}
        aria-expanded={open}
        aria-label={reachable ? 'Emergency contacts' : 'No emergency contact number'}
        onClick={() => setOpen(o => !o)}
      >
        {reachable ? <PhoneIcon aria-hidden="true" /> : <ExclamationTriangleIcon aria-hidden="true" />}
        Emergency
      </button>
      {open && (
        <span className="sp-popover sp-emergency__panel" role="dialog" aria-label="Emergency contacts">
          {contacts.length === 0 && <span className="sp-emergency__none">No emergency contact on file</span>}
          {contacts.map((c, i) => (
            <span key={i} className="sp-emergency__contact">
              <span className="sp-emergency__role">{c.role}</span>
              <span>
                <strong>{c.name}</strong>
                {c.relationship && <span className="sp-emergency__rel"> ({c.relationship})</span>}
              </span>
              {c.phone ? (
                <span className="sp-emergency__links">
                  <a href={telLink(c.phone)}>{c.phone}</a>
                  <a href={waLink(c.phone)} target="_blank" rel="noopener noreferrer">WhatsApp</a>
                </span>
              ) : (
                <span className="sp-emergency__none">No phone number on file</span>
              )}
            </span>
          ))}
        </span>
      )}
    </span>
  );
}

// items: [{ label, onSelect, danger? }]
export function SessionActionsMenu({ items }) {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, setOpen);
  return (
    <span className="sp-menu" ref={ref}>
      <button
        type="button"
        className="sp-menu__toggle"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Session actions"
        onClick={() => setOpen(o => !o)}
      >
        <EllipsisHorizontalIcon aria-hidden="true" />
      </button>
      {open && (
        <span className="sp-menu__list" role="menu">
          {items.map(item => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className={`sp-menu__item${item.danger ? ' sp-menu__item--danger' : ''}`}
              onClick={() => { setOpen(false); item.onSelect(); }}
            >
              {item.label}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}
